/**
 * parseScenarios - Extract structured scenario and trigger data from markdown
 * 
 * Parses deep research reports to extract:
 * - Scenario definitions with country-specific analysis
 * - Triggers to monitor by category
 */

import { DetailedScenario } from '../../components/cockpit/sections/ScenarioAnalysisSection'
import { DetailedTrigger } from '../../components/cockpit/sections/TriggersMonitorSection'

interface ParsedScenarioData {
  scenarios: DetailedScenario[]
  triggers: DetailedTrigger[]
}

/**
 * Parse scenario data from deep research markdown
 * 
 * NOTE: We aim for 3 canonical scenarios: Bull (upside), Base, Bear (downside)
 * The parser is selective to avoid over-matching casual mentions of "base case" etc.
 */
export function parseScenarioFromMarkdown(markdown: string): ParsedScenarioData {
  const scenarios: DetailedScenario[] = []
  const triggers: DetailedTrigger[] = []
  
  if (!markdown) {
    return { scenarios, triggers }
  }
  
  // Track which scenario types we've found to avoid duplicates
  const foundTones = new Set<string>()
  
  // Pattern 1: Explicit scenario headers like "Scenario 1: "Global Soft Landing" (Upside)"
  const scenarioHeaderPattern = /Scenario\s*(\d+)\s*:\s*[""]?([^"""\n]+)[""]?\s*\(([^)]+)\)/gi
  
  // Pattern 2: Section headers that are clearly scenario definitions
  // Must be a header (## or **) at start of line, followed by case type
  const caseHeaderPattern = /^(?:#{2,4}\s*|\*{2})(Base|Bull|Bear|Best|Worst|Optimistic|Pessimistic|Upside|Downside)\s*(?:Case|Scenario)\s*(?:\*{2})?[:\s]*$/gim
  
  // Pattern 3: Numbered scenario list items with probability
  // e.g., "1. **Base Case** (45% probability):" or "• Bull Case (30%):"
  const numberedScenarioPattern = /^(?:[\d]+\.\s*|\-\s*|•\s*)\*{0,2}(Base|Bull|Bear|Best|Worst|Optimistic|Pessimistic|Upside|Downside)\s*(?:Case|Scenario)\*{0,2}\s*(?:\((\d+)%?\s*(?:probability)?\))?[:\s]*/gim
  
  // Find all scenario sections - try Pattern 1 first (most specific)
  let scenarioMatches = Array.from(markdown.matchAll(scenarioHeaderPattern))
  
  for (let i = 0; i < scenarioMatches.length; i++) {
    const match = scenarioMatches[i]
    const scenarioNum = match[1]
    const scenarioName = match[2].trim()
    const subtitle = match[3].trim()
    
    // Determine scenario position in text
    const startIdx = match.index || 0
    const endIdx = scenarioMatches[i + 1]?.index || markdown.length
    const scenarioContent = markdown.slice(startIdx, endIdx)
    
    // Determine tone from subtitle
    let tone: 'upside' | 'base' | 'downside' | 'mixed' = 'base'
    const subtitleLower = subtitle.toLowerCase()
    if (subtitleLower.includes('upside') || subtitleLower.includes('optimistic')) {
      tone = 'upside'
    } else if (subtitleLower.includes('downside') || subtitleLower.includes('recession') || subtitleLower.includes('adverse')) {
      tone = 'downside'
    } else if (subtitleLower.includes('mixed') || subtitleLower.includes('tightening')) {
      tone = 'mixed'
    }
    
    // Extract description - first paragraph after the header
    const descriptionMatch = scenarioContent.match(/\n\n(.+?)(?:\n\n|$)/s)
    const description = descriptionMatch ? descriptionMatch[1].replace(/\n/g, ' ').trim() : undefined
    
    // Extract country-specific analysis
    const countryAnalysis = extractCountryAnalysis(scenarioContent)
    
    // Extract any triggers mentioned for this scenario
    const scenarioTriggerStrings = extractTriggersFromText(scenarioContent)
    const scenarioTriggers = scenarioTriggerStrings.map(t => ({ description: t }))
    
    scenarios.push({
      id: `scenario-${scenarioNum}`,
      name: scenarioName,
      subtitle,
      description,
      tone,
      countryAnalysis,
      triggers: scenarioTriggers,
    })
  }
  
  // Helper to determine tone and avoid duplicates
  const getToneFromName = (name: string): 'upside' | 'base' | 'downside' | 'mixed' => {
    const lower = name.toLowerCase()
    if (lower.includes('bull') || lower.includes('best') || lower.includes('optimistic') || lower.includes('upside')) {
      return 'upside'
    } else if (lower.includes('bear') || lower.includes('worst') || lower.includes('pessimistic') || lower.includes('downside')) {
      return 'downside'
    }
    return 'base'
  }
  
  const addScenarioIfUnique = (scenario: DetailedScenario): boolean => {
    // Only allow one of each tone type (bull, base, bear)
    if (foundTones.has(scenario.tone)) return false
    
    // Ensure we have meaningful content
    if (!scenario.description || scenario.description.length < 50) return false
    
    foundTones.add(scenario.tone)
    scenarios.push(scenario)
    return true
  }
  
  // If Pattern 1 didn't find scenarios, try Pattern 2 (section headers)
  if (scenarios.length === 0) {
    const caseMatches = Array.from(markdown.matchAll(caseHeaderPattern))
    
    for (let i = 0; i < caseMatches.length && scenarios.length < 3; i++) {
      const match = caseMatches[i]
      const caseName = match[1].trim()
      const tone = getToneFromName(caseName)
      
      // Skip if we already have this tone
      if (foundTones.has(tone)) continue
      
      // Find content between this match and the next section header
      const startIdx = (match.index || 0) + match[0].length
      const nextHeaderMatch = markdown.slice(startIdx).match(/^#{2,4}\s+|\n\*{2}[A-Z]/m)
      const endIdx = nextHeaderMatch ? startIdx + (nextHeaderMatch.index || 1000) : startIdx + 1500
      const caseContent = markdown.slice(startIdx, endIdx)
      
      // Extract description - first meaningful paragraph
      const descMatch = caseContent.match(/\n*(.+?)(?:\n\n|$)/s)
      let description = descMatch ? descMatch[1].replace(/\n/g, ' ').replace(/\*{1,2}/g, '').trim() : undefined
      
      // Clean up and validate
      if (description) {
        description = description.substring(0, 600)
        if (description.length >= 50) {
          addScenarioIfUnique({
            id: `scenario-${tone}`,
            name: `${caseName} Case`,
            subtitle: tone === 'upside' ? 'Optimistic outlook' : tone === 'downside' ? 'Risk scenario' : 'Expected outcome',
            description,
            tone,
          })
        }
      }
    }
  }
  
  // If still no scenarios, try Pattern 3 (numbered list items)
  if (scenarios.length === 0) {
    const numberedMatches = Array.from(markdown.matchAll(numberedScenarioPattern))
    
    for (let i = 0; i < numberedMatches.length && scenarios.length < 3; i++) {
      const match = numberedMatches[i]
      const caseName = match[1].trim()
      const probability = match[2] ? parseInt(match[2]) : undefined
      const tone = getToneFromName(caseName)
      
      // Skip if we already have this tone
      if (foundTones.has(tone)) continue
      
      // Find content after this match
      const startIdx = (match.index || 0) + match[0].length
      const nextMatch = numberedMatches[i + 1]
      const endIdx = nextMatch?.index || Math.min(startIdx + 1000, markdown.length)
      const caseContent = markdown.slice(startIdx, endIdx)
      
      // Extract description
      const descMatch = caseContent.match(/(.+?)(?:\n\n|\n[\d\-•*]|$)/s)
      let description = descMatch ? descMatch[1].replace(/\n/g, ' ').replace(/\*{1,2}/g, '').trim() : undefined
      
      if (description && description.length >= 50) {
        description = description.substring(0, 600)
        addScenarioIfUnique({
          id: `scenario-${tone}`,
          name: `${caseName} Case`,
          subtitle: probability ? `${probability}% probability` : undefined,
          description,
          probability,
          tone,
        })
      }
    }
  }
  
  // Parse "Triggers to Monitor" section
  const triggersSection = extractTriggersSection(markdown)
  if (triggersSection.length > 0) {
    triggers.push(...triggersSection)
  }
  
  return { scenarios, triggers }
}

/**
 * Extract country-specific analysis from scenario content
 */
function extractCountryAnalysis(content: string): DetailedScenario['countryAnalysis'] {
  const countries: DetailedScenario['countryAnalysis'] = []
  
  // Pattern to match country-specific sections like "UK under Global Soft Landing:" or "• UK under..."
  const countryPatterns = [
    /[•\-*]\s*(UK|United Kingdom|Oman|USA|United States|EU|China)\s+under\s+[^:]+:\s*([^•\-*]+)/gi,
    /(UK|United Kingdom|Oman|USA|United States|EU|China)\s+under\s+[^:]+:\s*([^•\-*\n]+)/gi,
  ]
  
  for (const pattern of countryPatterns) {
    const matches = Array.from(content.matchAll(pattern))
    for (const match of matches) {
      const country = normalizeCountryName(match[1])
      const analysis = match[2].trim()
      
      // Avoid duplicates
      if (!countries.find(c => c.country === country)) {
        // Extract any metrics mentioned
        const metrics = extractMetricsFromText(analysis)
        
        countries.push({
          country,
          analysis: analysis.substring(0, 500), // Truncate long analysis
          metrics,
        })
      }
    }
  }
  
  return countries.length > 0 ? countries : undefined
}

/**
 * Extract metrics from text (GDP, inflation, etc.)
 */
function extractMetricsFromText(text: string): { label: string; value: string; trend?: 'up' | 'down' | 'neutral' }[] {
  const metrics: { label: string; value: string; trend?: 'up' | 'down' | 'neutral' }[] = []
  
  // Pattern to match metrics like "GDP growth 2-3%" or "inflation 3%"
  const metricPatterns = [
    /(GDP\s*(?:growth)?)\s*(?:of\s*)?([-+]?\d+(?:\.\d+)?%?\s*(?:to\s*[-+]?\d+(?:\.\d+)?%?)?)/gi,
    /(inflation)\s*(?:of\s*)?([-+]?\d+(?:\.\d+)?%?\s*(?:to\s*[-+]?\d+(?:\.\d+)?%?)?)/gi,
    /(unemployment)\s*(?:of\s*)?([-+]?\d+(?:\.\d+)?%?\s*(?:to\s*[-+]?\d+(?:\.\d+)?%?)?)/gi,
    /(mortgage\s*rates?)\s*(?:of\s*)?([-+]?\d+(?:\.\d+)?%?\s*(?:to\s*[-+]?\d+(?:\.\d+)?%?)?)/gi,
  ]
  
  for (const pattern of metricPatterns) {
    const matches = Array.from(text.matchAll(pattern))
    for (const match of matches) {
      const label = match[1].trim()
      const value = match[2].trim()
      
      // Determine trend from value
      let trend: 'up' | 'down' | 'neutral' = 'neutral'
      if (value.startsWith('+') || value.startsWith('-')) {
        trend = value.startsWith('+') ? 'up' : 'down'
      }
      
      metrics.push({ label, value, trend })
    }
  }
  
  return metrics.slice(0, 4) // Limit to 4 metrics
}

/**
 * Extract triggers from text content
 */
function extractTriggersFromText(text: string): string[] {
  const triggers: string[] = []
  
  // Pattern to match "Trigger to watch:" or similar
  const triggerMatch = text.match(/Trigger(?:s)?\s+to\s+watch\s*:\s*([^.]+\.)/i)
  if (triggerMatch) {
    // Split by common delimiters
    const parts = triggerMatch[1].split(/[,;]/).map(s => s.trim()).filter(Boolean)
    triggers.push(...parts.slice(0, 5))
  }
  
  return triggers
}

/**
 * Extract the "Triggers to Monitor" section
 */
function extractTriggersSection(markdown: string): DetailedTrigger[] {
  const triggers: DetailedTrigger[] = []
  
  // Find "Triggers to Monitor" section
  const sectionPattern = /Triggers?\s+to\s+Monitor(?:\s+\(Summary\))?\s*:?\s*\n([\s\S]+?)(?=\n#{1,3}\s|$)/i
  const sectionMatch = markdown.match(sectionPattern)
  
  if (!sectionMatch) {
    return triggers
  }
  
  const content = sectionMatch[1]
  
  // Pattern to match category headers like "For the UK:" or "• For the UK:"
  const categoryPattern = /(?:[•\-*]\s*)?(For\s+(?:the\s+)?(\w+))\s*:?\s*([^•\-*]+?)(?=(?:[•\-*]\s*)?For\s+|$)/gis
  const categoryMatches = Array.from(content.matchAll(categoryPattern))
  
  for (const match of categoryMatches) {
    const categoryName = match[1].trim()
    const country = normalizeCountryName(match[2])
    const indicatorsText = match[3].trim()
    
    // Parse individual indicators
    const indicators = parseIndicators(indicatorsText)
    
    if (indicators.length > 0) {
      triggers.push({
        id: `triggers-${country.toLowerCase()}`,
        category: `Triggers for ${country}`,
        indicators,
      })
    }
  }
  
  // If no categories found, treat entire content as one category
  if (triggers.length === 0 && content.trim()) {
    const indicators = parseIndicators(content)
    if (indicators.length > 0) {
      triggers.push({
        id: 'triggers-general',
        category: 'Key Triggers',
        indicators,
      })
    }
  }
  
  return triggers
}

/**
 * Parse indicators from text content
 */
function parseIndicators(text: string): { label: string; description?: string; type?: 'metric' | 'event' | 'threshold'; status?: 'normal' | 'warning' | 'critical' }[] {
  const indicators: { label: string; description?: string; type?: 'metric' | 'event' | 'threshold'; status?: 'normal' | 'warning' | 'critical' }[] = []
  
  // Known indicator keywords for better labeling
  const indicatorKeywords = [
    { pattern: /inflation\s*rates?\s*\(CPI\)/i, label: 'Inflation Rates (CPI)', type: 'metric' as const },
    { pattern: /BoE\s*meeting\s*signals/i, label: 'BoE Meeting Signals', type: 'event' as const },
    { pattern: /fiscal\s*policy\s*announcements/i, label: 'Fiscal Policy Announcements', type: 'event' as const },
    { pattern: /trade\s*data/i, label: 'Trade Data', type: 'metric' as const },
    { pattern: /consumer\/business\s*confidence/i, label: 'Consumer/Business Confidence', type: 'metric' as const },
    { pattern: /GBP\s*exchange\s*rate/i, label: 'GBP Exchange Rate', type: 'metric' as const },
    { pattern: /gilt\s*yields?/i, label: 'Gilt Yields', type: 'metric' as const },
    { pattern: /global\s*oil\s*prices/i, label: 'Global Oil Prices', type: 'metric' as const },
    { pattern: /OPEC\+?\s*decisions/i, label: 'OPEC+ Decisions', type: 'event' as const },
    { pattern: /government\s*budget/i, label: 'Government Budget Updates', type: 'event' as const },
    { pattern: /FDI\s*(?:and\s*)?project\s*announcements/i, label: 'FDI & Project Announcements', type: 'event' as const },
    { pattern: /energy\s*commodity\s*prices/i, label: 'Energy Commodity Prices', type: 'metric' as const },
    { pattern: /PMI/i, label: 'PMI Indicators', type: 'metric' as const },
    { pattern: /unemployment/i, label: 'Unemployment Rate', type: 'metric' as const },
    { pattern: /house\s*prices/i, label: 'House Prices', type: 'metric' as const },
  ]
  
  // First try to match known patterns
  for (const kw of indicatorKeywords) {
    if (kw.pattern.test(text)) {
      const existingIdx = indicators.findIndex(i => i.label === kw.label)
      if (existingIdx === -1) {
        indicators.push({
          label: kw.label,
          type: kw.type,
          status: 'normal',
        })
      }
    }
  }
  
  // If no known patterns matched, try to split by commas/semicolons
  if (indicators.length === 0) {
    const parts = text.split(/[,;]\s*/).filter(p => p.trim().length > 5 && p.trim().length < 80)
    for (const part of parts.slice(0, 8)) {
      const cleaned = part.replace(/^\s*[\-•*]\s*/, '').trim()
      if (cleaned) {
        indicators.push({
          label: cleaned,
          type: 'metric',
          status: 'normal',
        })
      }
    }
  }
  
  return indicators
}

/**
 * Normalize country names
 */
function normalizeCountryName(name: string): string {
  const mapping: Record<string, string> = {
    'uk': 'UK',
    'united kingdom': 'UK',
    'oman': 'Oman',
    'usa': 'USA',
    'united states': 'USA',
    'eu': 'EU',
    'china': 'China',
  }
  return mapping[name.toLowerCase()] || name
}

export default parseScenarioFromMarkdown


